import { IconEye, IconEyeOff, IconInfoCircle } from '@tabler/icons-react';
import { Button, Card, Flex, Grid, Text } from '@mantine/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import MetricsSelector from '../../components/MetricsSelector';
import { daysAgoISODate, dateToEndOfDay, dateToStartOfDay, todayISODate } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';

const EmailsClickAnalyticsPage = () => {
  const fetch = useFetch();

  const [email, setEmail] = useState();
  const [clickTrackingStats, setClickTrackingStats] = useState();
  const [totalEmailClicks, setTotalEmailClicks] = useState();
  const [productBlocksTrackingIds, setProductBlocksTrackingIds] = useState();
  const [error, setError] = useState();
  const [emailLoaded, setEmailLoaded] = useState(false);
  const { accountId, domainId, emailId } = useParams();
  const initialPeriodDays = 7;

  // Store ISO date strings (YYYY-MM-DD)
  const [startDate, setStartDate] = useState(daysAgoISODate(initialPeriodDays - 1));
  const [endDate, setEndDate] = useState(todayISODate());
  const iframeRef = useRef(null);

  const [clickMapVisible, setClickMapVisible] = useState(true);
  const hasFetchedEmail = useRef(false);

  useEffect(() => {
    if (!hasFetchedEmail.current) {
      fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/emails/${emailId}`)
        .then(({ email }) => {
          setEmail(email);
          setProductBlocksTrackingIds(email.productBlocksClickTrackingIds);
          setEmailLoaded(true);
        })
        .catch(setError);
    }
  }, [accountId, domainId, emailId, fetch]);

  useEffect(() => {
    // Convert ISO date strings to full timestamps for the API
    const startTs = encodeURIComponent(dateToStartOfDay(startDate, 'local'));
    const endTs = encodeURIComponent(dateToEndOfDay(endDate, 'local'));
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/emails/${emailId}/emailClickTrackingStats/${startTs}/${endTs}`
    )
      .then(({ trackingData, totalEmailClicks }) => {
        setClickTrackingStats(trackingData);
        setTotalEmailClicks(totalEmailClicks);
      })
      .catch(setError);
  }, [accountId, domainId, emailId, endDate, fetch, startDate]);

  const isClickTrackingStatsEmpty = !clickTrackingStats || Object.keys(clickTrackingStats)?.length === 0;
  const targetDocument = iframeRef?.current?.contentDocument;

  const positionOverlays = useCallback(() => {
    if (!targetDocument) {
      return;
    }

    const emailContainer = targetDocument?.querySelector('.es-wrapper');

    if (!emailContainer) {
      return;
    }

    const productBlocks = emailContainer.querySelectorAll('.product-block');

    productBlocks.forEach((productBlock, blockIndex) => {
      const extBlockImages = productBlock.querySelectorAll('.ext-product-image');

      extBlockImages.forEach((image, index) => {
        const imgElement = image.querySelector('img');
        if (!imgElement) {
          return;
        }
        const positionElement = document.createElement('div');
        positionElement.className = 'image-placeholder-position';
        positionElement.textContent = `Product Position ${index + 1}`;
        imgElement && imgElement.replaceWith(positionElement);
      });

      const products = productBlock.querySelectorAll('.product-card');
      products.forEach((product, productIndex) => {
        const blockTrackingIds = productBlocksTrackingIds?.[blockIndex.toString()];
        const productTrackingId = blockTrackingIds?.[productIndex];

        if (!productTrackingId) {
          return;
        }
        product.setAttribute('data-rlv-ctid', productTrackingId);
      });
    });

    const clickTrackingTargets = emailContainer.querySelectorAll('[data-rlv-ctid]');

    clickTrackingTargets.forEach((target) => {
      const clickId = target.getAttribute('data-rlv-ctid');

      let clickCount = 0;
      if (clickTrackingStats?.[clickId]) {
        clickCount = clickTrackingStats[clickId];
      }

      if (clickCount === 0) {
        return;
      }

      const clickPercentage = (clickCount / totalEmailClicks) * 100;
      const formattedClickPercentage =
        clickPercentage % 1 === 0 ? `${clickPercentage.toFixed(0)}%` : `${clickPercentage.toFixed(2)}%`;
      const clickCountString = `${clickCount} | ${formattedClickPercentage}`;
      const existingOverlay = targetDocument?.getElementById(clickId);

      if (existingOverlay) {
        existingOverlay.textContent = clickCountString;
        return;
      }

      const overlay = targetDocument?.createElement('div');
      overlay.className = 'click-overlay';
      overlay.id = clickId;
      overlay.textContent = clickCountString;

      const targetRect = target.getBoundingClientRect();
      const emailContainerRect = emailContainer.getBoundingClientRect();
      overlay.style.left = `${targetRect.left - emailContainerRect.left}px`;
      overlay.style.top = `${targetRect.top - emailContainerRect.top}px`;

      overlay.addEventListener('mouseover', () => {
        target.parentElement.classList.add('click-tracking-highlight');
      });

      overlay.addEventListener('mouseout', () => {
        target.parentElement.classList.remove('click-tracking-highlight');
      });

      emailContainer.appendChild(overlay);
    });
  }, [clickTrackingStats, productBlocksTrackingIds, targetDocument, totalEmailClicks]);

  useEffect(() => {
    const handleLoad = () => {
      const esWrapper = targetDocument?.querySelector('.es-wrapper');

      if (!esWrapper) {
        return;
      }

      // Inject styles into the iframe document
      if (targetDocument) {
        // Clone and append all styles and links from the main document to the iframe document
        const headElements = document?.querySelectorAll('style, link[rel="stylesheet"]');
        headElements.forEach((element) => {
          targetDocument.head.appendChild(element.cloneNode(true));
        });
      }

      let emailOverlay = esWrapper.querySelector('.email-overlay');
      if (!emailOverlay) {
        emailOverlay = targetDocument?.createElement('div');
        emailOverlay.classList.add('email-overlay');
        esWrapper.appendChild(emailOverlay);
      }

      positionOverlays();
    };

    const checkImagesLoaded = () => {
      const images = Array.from(targetDocument?.querySelectorAll('img') || []);
      const allImagesLoaded = images.every((img) => img.complete);

      if (allImagesLoaded) {
        handleLoad();
      } else {
        const promises = images.map((img) => {
          return new Promise((resolve) => {
            if (img.complete) {
              resolve();
            } else {
              img.addEventListener('load', resolve);
              img.addEventListener('error', resolve);
            }
          });
        });

        Promise.all(promises).then(handleLoad);
      }
    };

    if (!emailLoaded || !email?.html) {
      return;
    }
    const onReadyStateChange = () => {
      if (targetDocument?.readyState === 'complete') {
        checkImagesLoaded();
      }
    };

    if (targetDocument?.readyState === 'complete') {
      checkImagesLoaded();
    } else {
      targetDocument?.addEventListener('readystatechange', onReadyStateChange);
    }

    return () => {
      targetDocument?.removeEventListener('readystatechange', onReadyStateChange);
    };
  }, [emailLoaded, email?.html, positionOverlays, isClickTrackingStatsEmpty, targetDocument]);

  const handleClickMapDisplay = () => {
    const overlays = targetDocument?.querySelectorAll('.click-overlay');
    overlays.forEach((overlay) => {
      overlay.style.display = clickMapVisible ? 'none' : 'block';
    });
  };

  const toggleClickMapVisibility = () => {
    setClickMapVisible(!clickMapVisible);
    handleClickMapDisplay();
  };

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Grid className="sticky-grid">
          <Grid.Col span="auto">
            <MetricsSelector
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              rowSize="12"
              scope={null}
            />
          </Grid.Col>
          <Grid.Col span="content">
            <Button size="sm" className="click-count-button" onClick={toggleClickMapVisibility}>
              {clickMapVisible ? <IconEyeOff size={14} /> : <IconEye size={14} />}{' '}
              {clickMapVisible ? 'Hide Click Count' : 'Show Click Count'}
            </Button>
          </Grid.Col>
        </Grid>

        {isClickTrackingStatsEmpty && (
          <Flex justify="center" align="center">
            <Text c="dimmed" bg="gray.1" style={{ marginBottom: 0, padding: 'var(--mantine-spacing-md)' }}>
              <IconInfoCircle size={14} /> No click analytics available for this email for the selected
              period.
            </Text>
          </Flex>
        )}

        <iframe
          ref={iframeRef}
          id="contentIframe"
          title="Email"
          width="100%"
          height="700pt"
          className="email-contents-wrapper"
          srcDoc={email?.html}
        />

        {error && <ErrorMessage message={`Error loading email content: ${error?.message}`} />}
      </Card.Section>
    </Card>
  );
};

export default EmailsClickAnalyticsPage;
